import React, { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { chakra, Icon, Image, useFormControl, useMultiStyleConfig, } from "@chakra-ui/react";
import { useCombobox, useMultipleSelection } from "downshift";
import { RemoveScroll } from "react-remove-scroll";
import isEqual from "lodash/isEqual";
import { useVirtualizer } from "@tanstack/react-virtual";
import { matchSorter } from "match-sorter";
import { useFocusRing } from "react-aria";
import { SelectOption } from "./select-option";
import { SelectPopover, useSelectPopover } from "./select-popover";
import { SelectOptions } from "./select-options";
import { SelectEmptyState } from "./select-empty-state";
import { Spinner } from "./spinner";
import { useVirtualItems } from "./hooks/use-virtual-items";
import { SelectOptionLabel } from "./select-option-label";
import { SelectOptionAccessory } from "./select-option-accessory";
import { defaultCreateOptionMessage, defaultIsOptionDisabled, defaultOptionLabel, defaultOptionValue, } from "./lib/select-helpers";
import { SelectorIcon } from "./icons";
// Used to identify a tag input option for creating a new option, e.g. "Create 'something'"
const CreatableOption = Symbol("creatable option");
export function TagInput({ isDisabled = false, isLoading = false, isInvalid = false, isOptionDisabled = defaultIsOptionDisabled, emptyOptionsMessage = "No options", supportsCreatableOptions, createOptionMessage = defaultCreateOptionMessage, options, optionLabel = defaultOptionLabel, optionValue = defaultOptionValue, optionAccessory, placeholder = "", value, width = "xs", onChange, onClose, onCreateOption, onOpen, }) {
    const [isCreating, setIsCreating] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const selection = useMultipleSelection({
        selectedItems: value,
        onStateChange({ selectedItems: newSelectedItems, type }) {
            switch (type) {
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
                case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
                case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
                    onChange(newSelectedItems ?? []);
                    break;
                default:
                    break;
            }
        },
        onSelectedItemsChange({ selectedItems }) {
            onChange(selectedItems ?? []);
        },
    });
    const filteredOptions = useMemo(() => {
        // Exclude all options that are already selected
        const unselectedOptions = options.filter((option) => {
            const isSelected = value.some((valueItem) => {
                return isEqual(optionValue(option), valueItem);
            });
            return !isSelected;
        });
        if (inputValue.length === 0) {
            return unselectedOptions;
        }
        return matchSorter(unselectedOptions, inputValue, {
            keys: [optionLabel],
        });
    }, [options, optionLabel, optionValue, inputValue, value]);
    const tagInputOptions = useMemo(() => {
        return isCreating ? [CreatableOption] : filteredOptions;
    }, [isCreating, filteredOptions]);
    const formControl = useFormControl({
        isDisabled,
        isInvalid,
    });
    const optionsContainer = useRef(null);
    const { virtualItems, getVirtualItemKey, getVirtualItemIndex } = useVirtualItems({
        options: isCreating ? [CreatableOption] : filteredOptions,
        optionLabel,
    });
    const virtualizer = useVirtualizer({
        count: virtualItems.length,
        getItemKey: getVirtualItemKey,
        getScrollElement() {
            return optionsContainer.current;
        },
        estimateSize() {
            return 32;
        },
    });
    const popover = useSelectPopover();
    const combobox = useCombobox({
        isOpen: popover.isOpen,
        labelId: `${formControl.id}-label`,
        inputId: formControl.id,
        items: tagInputOptions,
        itemToString(option) {
            if (!option) {
                return "";
            }
            if (typeof option === "symbol") {
                return inputValue;
            }
            return optionLabel(option);
        },
        defaultHighlightedIndex: 0,
        selectedItem: null,
        inputValue,
        stateReducer(_state, { type, changes }) {
            switch (type) {
                case useCombobox.stateChangeTypes.ItemClick:
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                    return {
                        ...changes,
                        isOpen: true,
                        inputValue: "",
                    };
                default:
                    return changes;
            }
        },
        onInputValueChange(changes) {
            setInputValue(changes.inputValue ?? "");
        },
        onIsOpenChange({ isOpen }) {
            if (isOpen) {
                popover.open();
                onOpen?.();
            }
            else {
                popover.close();
                onClose?.();
            }
        },
        onStateChange({ type, selectedItem }) {
            if (type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
                type === useCombobox.stateChangeTypes.ItemClick) {
                if (!selectedItem) {
                    return;
                }
                if (typeof selectedItem === "symbol") {
                    if (typeof onCreateOption === "function") {
                        setIsCreating(false);
                        setInputValue("");
                        void onCreateOption(inputValue ?? "");
                    }
                    return;
                }
                selection.addSelectedItem(optionValue(selectedItem));
            }
        },
        onHighlightedIndexChange({ highlightedIndex }) {
            if (typeof highlightedIndex !== "number") {
                return;
            }
            if (highlightedIndex === -1) {
                virtualizer.scrollToOffset(0);
                return;
            }
            const virtualIndex = getVirtualItemIndex(highlightedIndex);
            try {
                const [offset] = virtualizer.getOffsetForIndex(virtualIndex);
                virtualizer.scrollToOffset(offset);
            }
            catch (error) {
                // `getOffsetForIndex` throws when `useVirtualizer` hasn't measured an item
                // with `highlightedIndex` yet and there's nothing we can do, but silence it.
                // See https://github.com/hightouchio/hightouch-ui/issues/225.
                if (error instanceof Error &&
                    error.message === "Unexpected undefined") {
                    return;
                }
                throw error;
            }
        },
    });
    useEffect(() => {
        if (supportsCreatableOptions &&
            inputValue.length > 0 &&
            filteredOptions.length === 0) {
            setIsCreating(true);
        }
    }, [supportsCreatableOptions, inputValue, filteredOptions]);
    useEffect(() => {
        if (filteredOptions.length > 0) {
            setIsCreating(false);
        }
    }, [filteredOptions]);
    // Reset input value to empty string, if user hasn't selected any option and input lost focus
    useEffect(() => {
        if (popover.isOpen) {
            return;
        }
        setInputValue("");
    }, [popover.isOpen]);
    const selectedOptions = useMemo(() => {
        const result = [];
        for (const valueItem of value) {
            const option = options.find((option) => {
                return isEqual(optionValue(option), valueItem);
            });
            if (option) {
                result.push(option);
            }
        }
        return result;
    }, [options, optionValue, value]);
    // Update popover position as user is typing, since filtering
    // can change the number of displayed options and as a result,
    // that can change the height of the popover.
    // Also update popover position when number of selected options
    // changes, since that can change the height of the tag input.
    useEffect(() => {
        popover.update();
    }, [inputValue, selectedOptions.length]);
    const styles = useMultiStyleConfig("TagInput");
    const { isFocusVisible, focusProps } = useFocusRing({
        isTextInput: true,
        within: true,
    });
    const tagsRef = useRef(null);
    const inputRef = useRef(null);
    return (React.createElement(RemoveScroll, { enabled: popover.isOpen },
        React.createElement(chakra.div, { __css: styles["tagInput"], width: width, "aria-invalid": formControl["aria-invalid"], "aria-required": formControl["aria-required"], "aria-describedby": formControl["aria-describedby"], "aria-busy": isLoading ? "true" : undefined, "data-selected": selectedOptions.length > 0 ? "true" : undefined, "data-disabled": formControl.disabled ? "true" : undefined, "data-focus-visible": isFocusVisible ? "true" : undefined, "data-testid": "tag-input", ...popover.getTargetProps({
                onFocus(event) {
                    if (typeof focusProps.onFocus === "function") {
                        focusProps.onFocus(event);
                    }
                },
                onBlur(event) {
                    if (typeof focusProps.onBlur === "function") {
                        focusProps.onBlur(event);
                    }
                },
                onKeyDown(event) {
                    if (event.key === "Escape" && popover.isOpen) {
                        event.stopPropagation();
                    }
                },
            }) },
            React.createElement(chakra.div, { ref: tagsRef, __css: styles["tags"], onClick: (event) => {
                    // Focus text input when user clicks on whitespace
                    if (event.target === tagsRef.current) {
                        inputRef.current?.focus();
                    }
                } },
                selectedOptions.map((selectedOption, index) => {
                    const accessory = typeof optionAccessory === "function"
                        ? optionAccessory(selectedOption)
                        : undefined;
                    const hasImageAccessory = accessory?.type === "image";
                    return (React.createElement(chakra.div, { key: optionLabel(selectedOption), __css: styles["tag"], "data-disabled": formControl.disabled ? "true" : undefined, "data-has-image-accessory": hasImageAccessory ? "true" : undefined, "data-testid": "tag-input-selected-option", ...(formControl.disabled
                            ? {}
                            : selection.getSelectedItemProps({
                                selectedItem: optionValue(selectedOption),
                                index,
                            })) },
                        accessory?.type === "image" && (React.createElement(chakra.div, { __css: styles["tagAccessory"], "data-status": accessory.status },
                            React.createElement(Image, { __css: styles["tagAccessoryImage"], src: accessory.url }))),
                        React.createElement(chakra.span, { __css: styles["tagLabel"], title: optionLabel(selectedOption) }, optionLabel(selectedOption)),
                        !formControl.disabled && (React.createElement(chakra.span, { "aria-hidden": true, __css: styles["tagRemoveButton"], "data-testid": "tag-input-selected-option-remove", onClick: (event) => {
                                event.stopPropagation();
                                if (formControl.disabled) {
                                    return;
                                }
                                selection.removeSelectedItem(optionValue(selectedOption));
                            } },
                            React.createElement(Icon, { as: XMarkIcon, __css: styles["tagRemoveButtonIcon"] })))));
                }),
                React.createElement(chakra.div, { __css: styles["inputContainer"], ...combobox.getComboboxProps() },
                    React.createElement(chakra.input
                    // Ask 1Password to not autofill this input and not show their icon inside
                    , { "data-1p-ignore": true, __css: styles["input"], type: "text", placeholder: value.length === 0 ? placeholder : undefined, disabled: formControl.disabled, "data-testid": "tag-input-input", ...combobox.getInputProps({
                            ...selection.getDropdownProps({
                                ref: inputRef,
                                preventKeyAction: popover.isOpen,
                            }),
                            onFocus(event) {
                                combobox.openMenu();
                                formControl.onFocus(event);
                            },
                            onBlur: formControl.onBlur,
                        }) }))),
            isLoading ? (React.createElement(Spinner, { size: "sm" })) : (React.createElement(chakra.button, { __css: styles["toggleButton"], type: "button", disabled: formControl.disabled, "data-testid": "tag-input-toggle", ...combobox.getToggleButtonProps() },
                React.createElement(Icon, { as: SelectorIcon, __css: styles["toggleButtonIcon"], color: "text.secondary" })))),
        React.createElement(SelectPopover, { isOpen: popover.isOpen, "data-testid": "tag-input-popover", ...combobox.getMenuProps(popover.getPopoverProps()) },
            virtualItems.length === 0 && (React.createElement(SelectEmptyState, null, emptyOptionsMessage)),
            virtualItems.length > 0 && (React.createElement(SelectOptions, { ref: optionsContainer, virtualizer: virtualizer, virtualItems: virtualItems }, (virtualItem, index) => {
                if (virtualItem.type === "option") {
                    const accessory = optionAccessory?.(virtualItem.option);
                    return (React.createElement(SelectOption, { "data-index": index, ...combobox.getItemProps({
                            ref: virtualizer.measureElement,
                            item: virtualItem.option,
                            index: virtualItem.index,
                            disabled: isOptionDisabled(virtualItem.option),
                        }) },
                        accessory && (React.createElement(SelectOptionAccessory, { accessory: accessory })),
                        React.createElement(SelectOptionLabel, null, optionLabel(virtualItem.option))));
                }
                if (virtualItem.type === "creatable-option") {
                    return (React.createElement(SelectOption, { isSelected: true, "data-index": 0, ...combobox.getItemProps({
                            ref: virtualizer.measureElement,
                            item: CreatableOption,
                            index: 0,
                        }) },
                        React.createElement(SelectOptionLabel, null, createOptionMessage(inputValue))));
                }
                return null;
            })))));
}
